/**
 * Paso 2 dentro de la PWA instalada (T033; research R-5): "Tengo un código".
 *
 * Canjea el código de traspaso por un token de invitación (`POST /activation/redeem-code`). La API
 * **rota** el token: el enlace original de Safari deja de funcionar y aquí se guarda el token nuevo
 * antes de seguir a `/registrar` (wiring §4.2, DECISIÓN de `redeem-code`).
 *
 * Un `410 HANDOFF_CODE_INVALID` no saca de la pantalla: se muestra el error en línea y se puede
 * reintentar (el código pudo teclearse mal o vencer a los 15 min).
 *
 * Piloto 2026-09-30: la lógica (`useHandoffCodeForm`) y el campo (`HandoffCodeField`) se exportan
 * para que "Este dispositivo no está registrado" pida el código en la misma pantalla sin duplicar
 * el canje ni sus errores.
 */
import { useState } from 'react';

import { HANDOFF_CODE_LENGTH } from '@fermin/contracts';

import { Button } from '../../components/ui/button';
import { CodeInput } from '../../components/ui/code-input';
import { Screen } from '../../components/ui/screen';
import { Stepper } from '../../components/ui/stepper';
import { t } from '../../i18n';

import { useRedeemHandoffCode } from './api';
import { isInvitationSuperseded } from './link-invalid';

export interface HandoffCodeForm {
  code: string;
  setCode: (next: string) => void;
  /** Canjea `value` si tiene los 6 caracteres y no hay un canje en curso. */
  submit: (value: string) => void;
  complete: boolean;
  pending: boolean;
  invalid: boolean;
  /** 005 · QA-9: el código pertenecía a una invitación ya reemplazada por una nueva. */
  superseded: boolean;
}

/** Estado y canje del código de traspaso (`POST /activation/redeem-code`). */
export function useHandoffCodeForm(onRedeemed: (token: string) => void): HandoffCodeForm {
  const [code, setCodeState] = useState('');
  const redeem = useRedeemHandoffCode();

  function submit(value: string): void {
    if (value.length !== HANDOFF_CODE_LENGTH || redeem.isPending) {
      return;
    }
    redeem.mutate(value, { onSuccess: (data) => onRedeemed(data.token) });
  }

  return {
    code,
    setCode: (next) => {
      redeem.reset();
      setCodeState(next);
    },
    submit,
    complete: code.length === HANDOFF_CODE_LENGTH,
    pending: redeem.isPending,
    invalid: redeem.isError,
    superseded: isInvitationSuperseded(redeem.error),
  };
}

export interface HandoffCodeFieldProps {
  form: HandoffCodeForm;
  autoFocus?: boolean;
}

/** Las 6 celdas del código y su ayuda (o el error en línea, anunciado como `alert`). */
export function HandoffCodeField({ form, autoFocus = false }: HandoffCodeFieldProps) {
  return (
    <>
      <CodeInput
        value={form.code}
        onChange={form.setCode}
        onComplete={form.submit}
        invalid={form.invalid}
        disabled={form.pending}
        autoFocus={autoFocus}
        describedBy="handoff-code-help"
      />
      <p
        id="handoff-code-help"
        role={form.invalid ? 'alert' : undefined}
        className={
          form.invalid ? 'text-fm-sm text-fm-danger-text' : 'text-fm-sm text-fm-text-secondary'
        }
      >
        {form.invalid
          ? form.superseded
            ? t('activation.invalid.superseded')
            : t('activation.code.invalid')
          : t('common.codeInput.help')}
      </p>
    </>
  );
}

export interface HandoffCodeSubmitProps {
  form: HandoffCodeForm;
}

/** Botón primario "Continuar" del código (deshabilitado hasta tener los 6 caracteres). */
export function HandoffCodeSubmit({ form }: HandoffCodeSubmitProps) {
  return (
    <Button
      disabled={!form.complete}
      loading={form.pending}
      loadingText={t('common.states.verifying')}
      onClick={() => form.submit(form.code)}
    >
      {t('common.buttons.continue')}
    </Button>
  );
}

export interface HandoffCodeScreenProps {
  /** Recibe el token de invitación devuelto por la API. */
  onRedeemed: (token: string) => void;
  /** "No tengo código": lleva a pedir una re-vinculación (US3). */
  onNoCode: () => void;
}

export function HandoffCodeScreen({ onRedeemed, onNoCode }: HandoffCodeScreenProps) {
  const form = useHandoffCodeForm(onRedeemed);

  return (
    <Screen
      data-testid="activation-code"
      header={<Stepper current={2} />}
      title={t('activation.code.title')}
      lead={t('activation.code.lead')}
      footer={
        <>
          <HandoffCodeSubmit form={form} />
          <Button variant="ghost" onClick={onNoCode}>
            {t('activation.code.noCode')}
          </Button>
        </>
      }
    >
      <HandoffCodeField form={form} autoFocus />
    </Screen>
  );
}
